import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { ResizableImage } from './ResizableImage';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UIcon, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Code, Link2, Image as ImgIcon, Table as TableIcon,
  AlignLeft, AlignCenter, AlignRight, Undo, Redo, Repeat, Database, Grid3x3,
} from 'lucide-react';
import GalleryPicker from './GalleryPicker';
import { DataTableNode, DataTableDialog } from './DataTableNode';
import { LayoutTable, RepeatableTableRow, StyledTableCell, StyledTableHeader, RowResize, rowsToTable } from './tableExtensions';
import { Btn, TableToolbar, TextToTableDialog } from './TableTools';
import LinkDialog from './LinkDialog';
import { useAuth } from '../context/AuthContext';
import { normalizeUrl } from '../lib/normalizeUrl';


export default function RichTextEditor({ value, onChange, datasets = [], fields = null }) {
  const { getArticleFields } = useAuth();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [defaultFields, setDefaultFields] = useState(window.__ARTICLE_FIELDS__ || []);
  useEffect(() => {
    if (fields || defaultFields.length) return;
    getArticleFields().then((d) => { window.__ARTICLE_FIELDS__ = d.fields || []; setDefaultFields(d.fields || []); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);
  const fieldOptions = fields || defaultFields;
  const [dtOpen, setDtOpen] = useState(false);
  const [t2t, setT2t] = useState(null);
  const [linkDlg, setLinkDlg] = useState(null);
  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Link.configure({ openOnClick: false, autolink: true, linkOnPaste: true, defaultProtocol: 'https', protocols: ['http', 'https', 'mailto', 'tel'], HTMLAttributes: { rel: 'noopener noreferrer' } }),
      ResizableImage,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Write your article… use Insert field for mailmerge tokens like {{first_name}}' }),
      LayoutTable.configure({ resizable: true }),
      RepeatableTableRow,
      StyledTableHeader,
      StyledTableCell,
      RowResize,
      DataTableNode,
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: 'tiptap-content', 'data-testid': 'rte-content' } },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const insertField = (token) => editor.chain().focus().insertContent(`{{${token}}}`).run();
  const setLink = () => {
    const { from: f, to: t, empty: e } = editor.state.selection;
    setLinkDlg({ href: editor.getAttributes('link').href || '', text: e ? '' : editor.state.doc.textBetween(f, t, ' '), hasSelection: !e || editor.isActive('link') });
  };
  const applyLink = ({ href, text, newTab }) => {
    const attrs = { href: normalizeUrl(href), target: newTab ? '_blank' : null };
    const chain = editor.chain().focus();
    if (linkDlg.hasSelection) chain.extendMarkRange('link').setLink(attrs).run();
    else chain.insertContent({ type: 'text', text: text || href, marks: [{ type: 'link', attrs }] }).run();
    setLinkDlg(null);
  };
  const removeLink = () => { editor.chain().focus().extendMarkRange('link').unsetLink().run(); setLinkDlg(null); };
  const addImage = () => setPickerOpen(true);
  const insertTable = () => editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run();
  const markRepeat = (key) => {
    // Set data-repeat on the currently-selected table row.
    if (!editor.isActive('tableCell') && !editor.isActive('tableHeader')) {
      window.alert('Place the cursor inside a table row first, then choose the dataset to repeat it.');
      return;
    }
    editor.chain().focus().updateAttributes('tableRow', { 'data-repeat': key || null }).run();
  };
  const inTable = editor.isActive('table');
  const { from, to, empty } = editor.state.selection;
  const openTextToTable = () => { if (!empty) setT2t({ from, to, text: editor.state.doc.textBetween(from, to, '\n', '\n') }); };
  const insertTextTable = (rows, header) => {
    editor.chain().focus().deleteRange({ from: t2t.from, to: t2t.to }).insertContentAt(t2t.from, rowsToTable(rows, header)).run();
    setT2t(null);
  };

  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b" style={{ borderColor: 'rgba(0,0,0,0.1)', background: 'var(--cream)' }}>
        <Btn testid="rte-bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold"><Bold size={16} /></Btn>
        <Btn testid="rte-italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic"><Italic size={16} /></Btn>
        <Btn testid="rte-underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline"><UIcon size={16} /></Btn>
        <Btn testid="rte-strike" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough"><Strikethrough size={16} /></Btn>
        <span className="w-px h-5 mx-1" style={{ background: 'rgba(0,0,0,0.12)' }} />
        <Btn testid="rte-h1" active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1"><Heading1 size={16} /></Btn>
        <Btn testid="rte-h2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2"><Heading2 size={16} /></Btn>
        <Btn testid="rte-h3" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3"><Heading3 size={16} /></Btn>
        <input type="color" title="Text colour" data-testid="rte-color" onChange={(e) => editor.chain().focus().setColor(e.target.value).run()} className="w-6 h-6 p-0 border-0 bg-transparent cursor-pointer" />
        <span className="w-px h-5 mx-1" style={{ background: 'rgba(0,0,0,0.12)' }} />
        <Btn testid="rte-ul" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list"><List size={16} /></Btn>
        <Btn testid="rte-ol" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list"><ListOrdered size={16} /></Btn>
        <Btn testid="rte-quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote"><Quote size={16} /></Btn>
        <Btn testid="rte-code" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code block"><Code size={16} /></Btn>
        <span className="w-px h-5 mx-1" style={{ background: 'rgba(0,0,0,0.12)' }} />
        <Btn testid="rte-left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align left"><AlignLeft size={16} /></Btn>
        <Btn testid="rte-center" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align centre"><AlignCenter size={16} /></Btn>
        <Btn testid="rte-right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align right"><AlignRight size={16} /></Btn>
        <span className="w-px h-5 mx-1" style={{ background: 'rgba(0,0,0,0.12)' }} />
        <Btn testid="rte-link" active={editor.isActive('link')} onClick={setLink} title="Link"><Link2 size={16} /></Btn>
        <Btn testid="rte-image" onClick={addImage} title="Image"><ImgIcon size={16} /></Btn>
        <Btn testid="rte-table" onClick={insertTable} title="Insert table"><TableIcon size={16} /></Btn>
        <Btn testid="rte-text-to-table" onClick={openTextToTable} disabled={empty} title={empty ? 'Select some lines of text first, then convert them to a table' : 'Convert selected text to a table (tab, comma, bar…)'}><Grid3x3 size={16} /></Btn>
        <Btn testid="rte-data-table" onClick={() => setDtOpen(true)} title="Insert a data table (rows selected by tag)"><Database size={16} /></Btn>
        <span className="w-px h-5 mx-1" style={{ background: 'rgba(0,0,0,0.12)' }} />
        <Btn testid="rte-undo" onClick={() => editor.chain().focus().undo().run()} title="Undo"><Undo size={16} /></Btn>
        <Btn testid="rte-redo" onClick={() => editor.chain().focus().redo().run()} title="Redo"><Redo size={16} /></Btn>
      </div>

      {inTable && <TableToolbar editor={editor} />}

      <div className="flex flex-wrap items-center gap-2 px-2 py-1.5 border-b text-xs" style={{ borderColor: 'rgba(0,0,0,0.08)', background: '#fbfaf6' }}>
        <span className="font-title" style={{ color: 'var(--olive)' }}>Insert field:</span>
        <select data-testid="rte-field-select" defaultValue="" onChange={(e) => { if (e.target.value) { insertField(e.target.value); e.target.value = ''; } }}
          className="text-xs px-2 py-1 rounded border" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
          <option value="">{fieldOptions.length ? 'Choose…' : 'Loading fields…'}</option>
          {fieldOptions.map((f) => <option key={f.token} value={f.token}>{f.label} — {`{{${f.token}}}`}</option>)}
        </select>
        {datasets.length > 0 && (
          <>
            <span className="font-title inline-flex items-center gap-1" style={{ color: 'var(--olive)' }}><Repeat size={12} /> Repeat current row for:</span>
            <select data-testid="rte-repeat-select" defaultValue="" onChange={(e) => { markRepeat(e.target.value); e.target.value = ''; }}
              className="text-xs px-2 py-1 rounded border" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
              <option value="">(none)</option>
              {datasets.map((d) => { const lbl = `${d.label} → ${d.fields.map((x) => `{{${x}}}`).join(', ')}`; return <option key={d.key} value={d.key}>{lbl}</option>; })}
            </select>
          </>
        )}
      </div>

      <EditorContent editor={editor} />
      {linkDlg && <LinkDialog initialHref={linkDlg.href} initialText={linkDlg.text} hasSelection={linkDlg.hasSelection} onClose={() => setLinkDlg(null)} onApply={applyLink} onRemove={removeLink} />}
      {t2t && <TextToTableDialog text={t2t.text} onClose={() => setT2t(null)} onInsert={insertTextTable} />}
      {dtOpen && <DataTableDialog onClose={() => setDtOpen(false)} onInsert={(cfg) => { editor.chain().focus().insertDataTable(cfg).run(); setDtOpen(false); }} />}
      <GalleryPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(url) => editor.chain().focus().setImage({ src: url }).run()} title="Insert a graphic" />
    </div>
  );
}
